import React from 'react';
import { Tooltip } from 'antd';
import {
  GanttDependency,
  FlattenedTask,
  TimelineConfig,
  DependencyTypeLabels,
  CriticalPathColor,
} from '../types/gantt.types';
import { calculateDependencyPath } from '../utils/dependencyPathUtils';

interface DependencyLineProps {
  dependency: GanttDependency;
  fromTask: FlattenedTask;
  toTask: FlattenedTask;
  config: TimelineConfig;
  isCritical: boolean;
}

const DependencyLine: React.FC<DependencyLineProps> = ({
  dependency,
  fromTask,
  toTask,
  config,
  isCritical,
}) => {
  const pathData = calculateDependencyPath(fromTask, toTask, dependency.type, config);
  const strokeColor = isCritical ? CriticalPathColor : '#8c8c8c';
  const strokeWidth = isCritical ? 2 : 1.5;

  const tooltipContent = (
    <div style={{ fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Dependency</div>
      <div>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>From: </span>
        {fromTask.name}
      </div>
      <div>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>To: </span>
        {toTask.name}
      </div>
      <div>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>Type: </span>
        {DependencyTypeLabels[dependency.type]} ({dependency.type})
      </div>
      {dependency.lagDays !== undefined && dependency.lagDays !== 0 && (
        <div>
          <span style={{ color: 'rgba(255,255,255,0.65)' }}>Lag: </span>
          {dependency.lagDays} days
        </div>
      )}
    </div>
  );

  return (
    <Tooltip title={tooltipContent} placement="top" color="rgba(0,0,0,0.85)">
      <g className="gantt-dependency">
        {/* Dependency line */}
        <path
          d={pathData.path}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          style={{ pointerEvents: 'stroke' }}
        />

        {/* Arrow head */}
        <polygon points={pathData.arrowPoints} fill={strokeColor} />

        {/* Lag label (if applicable) */}
        {dependency.lagDays !== undefined && dependency.lagDays !== 0 && (
          <>
            <rect
              x={pathData.midPoint.x - 12}
              y={pathData.midPoint.y - 8}
              width={24}
              height={16}
              rx={3}
              fill="#fff"
              stroke={strokeColor}
              strokeWidth={1}
            />
            <text
              x={pathData.midPoint.x}
              y={pathData.midPoint.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={10}
              fill="#595959"
            >
              {dependency.lagDays > 0 ? `+${dependency.lagDays}` : dependency.lagDays}
            </text>
          </>
        )}
      </g>
    </Tooltip>
  );
};

export default DependencyLine;
